import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { LoaderCircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        // purple: "bg-purple-500 text-primary-foreground hover:bg-purple-600",
        "purple-griadient":
          "bg-gradient-to-tr from-[#6e36ff] to-[#ff34c3] text-white/90 hover:text-white relative brightness-100 hover:brightness-110 before:absolute z-1 before:z-[-1] before:inset-0 before:bg-gradient-to-tr before:from-inherit before:to-inherit before:blur-[4px] before:opacity-50",
        "blue-griadient":
          "bg-gradient-to-tr from-[#0010f4] to-[#00E3F4] text-white/90 hover:text-white relative brightness-100 hover:brightness-110 before:absolute z-1 before:z-[-1] before:inset-0 before:bg-gradient-to-tr before:from-inherit before:to-inherit before:blur-[4px] before:opacity-50",
        "red-griadient":
          "bg-gradient-to-tr from-[#ff3636] to-[#ff34c9] text-white/90 hover:text-white relative brightness-100 hover:brightness-110 before:absolute z-1 before:z-[-1] before:inset-0 before:bg-gradient-to-tr before:from-inherit before:to-inherit before:blur-[4px] before:opacity-50",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  ...props
}) {
  const Comp = asChild ? Slot : "button";

  if (!asChild) {
    return (
      <button
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading && (
          <LoaderCircleIcon
            className="animate-spin"
            size={props.size === "sm" ? 16 : props.size === "lg" ? 20 : 18}
          />
        )}
        {children}
      </button>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={props.disabled}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
